import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { input } from '@angular/core';
import { signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { ErrorWithDetails } from './api';

/**
 * A reactive wrapper for managing asynchronous operations with loading states.
 *
 * Provides signals for tracking the state, value, and error of async operations,
 * making it easy to handle loading, success, and error states in Angular components.
 *
 * @template T The type of the value returned by the async operation
 *
 * @example
 * ```typescript
 * const wrapper = new AsyncWrapper(() => fetchUserData());
 * await wrapper.load();
 *
 * // In template: wrapper.state() === 'loading' | 'success' | 'error'
 * // Access value: wrapper.value()
 * // Access error: wrapper.error()
 * ```
 */
export class AsyncWrapper<T> {

  private loader?: () => Promise<T>;
  private _value = signal<T | null>(null);

  /** Readonly signal containing the loaded value, null if not loaded or failed */
  value = this._value.asReadonly();

  /** Signal containing any error that occurred during loading */
  error = signal<Error | ErrorWithDetails | null>(null);

  /** Signal tracking the current state of the async operation */
  state = signal<'idle' | 'loading' | 'error' | 'success'>('idle');
  $state = toObservable(this.state);

  ready = toObservable(this.state).pipe(
    filter(state => state === 'success'),
    map(_ => this.value())
  )

  /**
   * Creates a new AsyncWrapper instance.
   *
   * @param loader Optional function that returns a Promise to be executed when load() is called
   */
  constructor(loader?: () => Promise<T>) {
    this.loader = loader;
  }

  /**
   * Executes the async operation and updates the wrapper's state accordingly.
   *
   * Sets state to 'loading', then either 'success' with the resolved value
   * or 'error' with the caught error.
   *
   * @param callback Optional loader function to use instead of the constructor loader
   * @throws Error if no loader function is provided
   */
  async load(callback?: () => Promise<T>) {
    const loader = callback || this.loader;
    if (!loader) {
      throw new Error('No loader provided');
    }
    this.setLoading();
    try {
      const value = await loader();
      this.set(value);
    } catch (error) {
      this.setError(error as Error);
    }
  }

  /**
   * Get value or throw error if not loaded
   */
  valueOrThrow(): T {
    const value = this.value();
    if (!value) {
      throw new Error('Value not loaded');
    }
    return value;
  }

  /**
   * Manually sets the value and updates state to 'success'.
   * Clears any existing error.
   *
   * @param value The value to set
   */
  set(value: T) {
    this._value.set(value);
    this.state.set('success');
    this.error.set(null);
  }

  /**
   * Manually sets an error and updates state to 'error'.
   * Clears any existing value.
   *
   * @param error The error to set
   */
  setError(error: Error) {
    this.error.set(error);
    this.state.set('error');
    this._value.set(null);
  }

  /**
   * Manually sets the state to 'loading'.
   * Clears any existing value and error.
   */
  setLoading() {
    this.state.set('loading');
    this.error.set(null);
    this._value.set(null);
  }
}

/**
 * Factory function for creating AsyncWrapper instances with optional auto-loading.
 *
 * Provides a convenient way to create and optionally immediately start loading
 * an AsyncWrapper instance.
 *
 * @template T The type of the value returned by the async operation
 * @param loader Optional function that returns a Promise to be executed
 * @param options Configuration options
 * @param options.autoLoad If true, immediately calls load() on the created wrapper
 * @returns A new AsyncWrapper instance
 *
 * @example
 * ```typescript
 * // Create wrapper that loads immediately
 * const userWrapper = asyncWrapper(() => fetchUser(id), { autoLoad: true });
 *
 * // Create wrapper for manual loading
 * const dataWrapper = asyncWrapper(() => fetchData());
 * await dataWrapper.load();
 * ```
 */
export function asyncWrapper<T>(loader?: () => Promise<T>, { autoLoad = false } = {}) {
  const wrapper = new AsyncWrapper<T>(loader);
  if (autoLoad) {
    wrapper.load();
  }
  return wrapper;
}


@Component({
  selector: 'risr-async',
  templateUrl: './async-wrapper.html',
  styles: [
    `
    .error {
        color: red;
    }
    .error-message {
        font-weight: 600;
        font-size: 16px;
        margin-bottom: 12px;
        padding: 12px 16px;
        background: #ffebee;
        border-left: 4px solid #f44336;
        border-radius: 4px;
    }
    .error-details {
        background: #fff5f5;
        border: 1px solid #ffcdd2;
        padding: 12px;
        border-radius: 4px;
        font-size: 12px;
        white-space: pre-wrap;
        word-break: break-word;
    }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule]
})
export class AsyncWrapperComponent {
  wrapper = input.required<AsyncWrapper<any>>();

  isErrorWithDetails(error: Error | ErrorWithDetails | null): error is ErrorWithDetails {
    return error instanceof ErrorWithDetails;
  }
}
